import React, { useEffect, useMemo, useState } from "react";
import { Button, Collapse, Empty, Form, InputNumber, Modal, Select, Spin, Table, Tag, Typography, message } from "antd";
import { PlusOutlined, TagsOutlined, DeleteOutlined } from "@ant-design/icons";
import { fetchMiscMode } from "../qv4/models/shared/miscModeModel";
import { fetchCostPrices, saveCostPriceRange, disableCostPriceRange } from "../qv4/models/shared/costPricesModel";
import { ManageTypesTabsModal } from "../qv4/views/shared/ManageTypesTabsModal";

// Three independent dimensions a margin rule can be scoped by, each backed
// by its own uzd_misc mode (same {nextValue, types} shape the customer_type
// list already used). A rule needs at least one of the three set, not all
// — the other two may be left blank on that rule. chipSuffix is the word
// appended after the type's own label in a "Types" chip (e.g. value "B2B"
// on customer_type -> chip "B2B Customer").
// customer_type carries the same B2B/B2C seed CustomerManagement's own
// "Manage Customer Types" button uses (same mode — whichever entry point
// opens it first against a blank slate should seed the same defaults).
// make_type/parts_type are genuinely new dimensions with no sensible seed.
const DIMENSIONS = [
  {
    key: "customer_type", mode: "customer_type", label: "Customer Type", chipSuffix: "Customer",
    defaultTypes: [{ value: 0, label: "B2B" }, { value: 1, label: "B2C" }], defaultNextValue: 2,
  },
  { key: "make_type", mode: "make_type", label: "Make Type", chipSuffix: "Make", defaultTypes: [], defaultNextValue: 0 },
  { key: "parts_type", mode: "parts_type", label: "Parts Type", chipSuffix: "Part", defaultTypes: [], defaultNextValue: 0 },
];

const TYPE_COLOR_PALETTE = ["geekblue", "purple", "orange", "green", "magenta", "gold", "volcano", "cyan"];

function optionsFromMode(data) {
  return Array.isArray(data?.types) ? data.types : [];
}

// Rows sharing the exact same (customer_type, make_type, parts_type) tuple
// (nulls included) belong to the same group — same "list" of price ranges,
// same margin-rule set. "∅" is just a stable placeholder so a blank
// dimension still produces a distinct, consistent key segment.
function groupKeyFor(row) {
  return DIMENSIONS.map(({ key }) => row[key] ?? "∅").join("|");
}

export function MarginRulesPanel() {
  const [rows, setRows] = useState([]);
  const [typeOptions, setTypeOptions] = useState({ customer_type: [], make_type: [], parts_type: [] });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ customer_type: null, make_type: null, parts_type: null });

  // A type combination (locked + pre-filled when editing an existing
  // group's whole set of ranges; editable + empty when starting a brand-new
  // group) plus one or more price ranges added/edited/removed via
  // Form.List, diffed against originalRangeIds and saved as individual
  // create/update/disable calls in one batch on submit.
  const [ruleSetOpen, setRuleSetOpen] = useState(false);
  const [lockedTypes, setLockedTypes] = useState(null);
  const [originalRangeIds, setOriginalRangeIds] = useState([]);
  const [ruleSetSaving, setRuleSetSaving] = useState(false);
  const [ruleSetForm] = Form.useForm();

  const [manageTypesOpen, setManageTypesOpen] = useState(false);

  const loadAll = () => {
    setLoading(true);
    return Promise.all([
      fetchCostPrices({ force: true }),
      fetchMiscMode("customer_type", { force: true }),
      fetchMiscMode("make_type", { force: true }),
      fetchMiscMode("parts_type", { force: true }),
    ]).then(([costRows, customerType, makeType, partsType]) => {
      setRows(costRows);
      setTypeOptions({
        customer_type: optionsFromMode(customerType),
        make_type: optionsFromMode(makeType),
        parts_type: optionsFromMode(partsType),
      });
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const labelMaps = useMemo(() => {
    const build = (list) => Object.fromEntries(list.map(o => [String(o.value), o.label]));
    return {
      customer_type: build(typeOptions.customer_type),
      make_type: build(typeOptions.make_type),
      parts_type: build(typeOptions.parts_type),
    };
  }, [typeOptions]);

  const colorMaps = useMemo(() => {
    const build = (list) => Object.fromEntries(list.map((o, i) => [String(o.value), TYPE_COLOR_PALETTE[i % TYPE_COLOR_PALETTE.length]]));
    return {
      customer_type: build(typeOptions.customer_type),
      make_type: build(typeOptions.make_type),
      parts_type: build(typeOptions.parts_type),
    };
  }, [typeOptions]);

  const filteredRows = useMemo(() => rows.filter(row =>
    DIMENSIONS.every(({ key }) => filters[key] == null || row[key] === filters[key])
  ), [rows, filters]);

  // One group per distinct type combination present in the (filtered) rows
  // — each is its own self-contained price-range list.
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const row of filteredRows) {
      const key = groupKeyFor(row);
      if (!byKey.has(key)) {
        byKey.set(key, { key, customer_type: row.customer_type, make_type: row.make_type, parts_type: row.parts_type, rows: [] });
      }
      byKey.get(key).rows.push(row);
    }
    return [...byKey.values()];
  }, [filteredRows]);

  const renderTypeChips = (typed) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {DIMENSIONS.filter(({ key }) => typed[key] != null).map(({ key, chipSuffix }) => (
        <Tag key={key} color={colorMaps[key][String(typed[key])] || "default"}>
          {(labelMaps[key][String(typed[key])] ?? typed[key])} {chipSuffix}
        </Tag>
      ))}
    </div>
  );

  // group === null starts a brand-new set: type Selects editable, one blank
  // range. Passing an existing group locks the Selects to its combination
  // and pre-fills every one of its current ranges (each carrying its id via
  // a hidden field) so the whole group can be edited — added to, changed,
  // or shrunk by removing a range — in one save.
  const openRuleSet = (group = null) => {
    const locked = group ? { customer_type: group.customer_type, make_type: group.make_type, parts_type: group.parts_type } : null;
    const existingRanges = group
      ? group.rows.map(r => ({ id: r.id, MinPrice: r.MinPrice, MaxPrice: r.MaxPrice, DeliveryCost: r.DeliveryCost, GP_Percentage: r.GP_Percentage }))
      : [{}];
    setLockedTypes(locked);
    setOriginalRangeIds(group ? group.rows.map(r => r.id) : []);
    ruleSetForm.resetFields();
    ruleSetForm.setFieldsValue({ ...(locked || {}), ranges: existingRanges });
    setRuleSetOpen(true);
  };

  const handleRuleSetSubmit = async () => {
    let values;
    try {
      values = await ruleSetForm.validateFields();
    } catch {
      return;
    }
    const { customer_type, make_type, parts_type, ranges } = values;
    setRuleSetSaving(true);
    try {
      const submittedIds = new Set(ranges.filter(r => r.id != null).map(r => r.id));
      const removedIds = originalRangeIds.filter(id => !submittedIds.has(id));

      let createdCount = 0, updatedCount = 0, disabledCount = 0, failed = false;

      for (const range of ranges) {
        const { id, ...fields } = range;
        const payload = { customer_type, make_type, parts_type, ...fields, ...(id != null ? { id } : {}) };
        const res = await saveCostPriceRange(payload);
        if (!res.success) { message.error(res.message || "Failed to save a range"); failed = true; break; }
        if (id != null) updatedCount += 1; else createdCount += 1;
      }

      if (!failed) {
        for (const id of removedIds) {
          const res = await disableCostPriceRange(id);
          if (!res.success) { message.error(res.message || "Failed to remove a range"); failed = true; break; }
          disabledCount += 1;
        }
      }

      if (createdCount + updatedCount + disabledCount > 0) await loadAll();

      if (!failed) {
        const parts = [];
        if (createdCount) parts.push(`${createdCount} added`);
        if (updatedCount) parts.push(`${updatedCount} updated`);
        if (disabledCount) parts.push(`${disabledCount} removed`);
        message.success(`Saved — ${parts.join(", ")}.`);
        setRuleSetOpen(false);
      }
    } finally {
      setRuleSetSaving(false);
    }
  };

  const groupRangeColumns = [
    { title: "Min Price", dataIndex: "MinPrice", key: "MinPrice", render: v => v?.toLocaleString() },
    { title: "Max Price", dataIndex: "MaxPrice", key: "MaxPrice", render: v => v?.toLocaleString() },
    { title: "Delivery Cost", dataIndex: "DeliveryCost", key: "DeliveryCost" },
    { title: "GP %", dataIndex: "GP_Percentage", key: "GP_Percentage", render: v => `${v}%` },
  ];

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
        {DIMENSIONS.map(({ key, label }) => (
          <Select
            key={key}
            size="middle"
            style={{ width: 160 }}
            allowClear
            placeholder={`${label}: All`}
            value={filters[key] ?? undefined}
            onChange={(val) => setFilters(prev => ({ ...prev, [key]: val ?? null }))}
            options={typeOptions[key]}
            optionFilterProp="label"
          />
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Button size="middle" icon={<TagsOutlined />} onClick={() => setManageTypesOpen(true)}>
            Manage Types
          </Button>
          <Button size="middle" type="primary" icon={<PlusOutlined />} onClick={() => openRuleSet()}>
            Add Rule Set
          </Button>
        </div>
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 10 }}>
        Rules are grouped by their type combination — each group is its own price-range list, edited as a
        whole. A rule needs at least one of Customer Type, Make Type, or Parts Type; the rest can be left blank.
      </Typography.Text>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin /></div>
      ) : groups.length === 0 ? (
        <Empty description="No margin rules yet" />
      ) : (
        <Collapse
          items={groups.map((group) => ({
            key: group.key,
            label: (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  {renderTypeChips(group)}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {group.rows.length} range{group.rows.length !== 1 ? "s" : ""}
                  </Typography.Text>
                </div>
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); openRuleSet(group); }}
                >
                  Edit Group
                </Button>
              </div>
            ),
            children: (
              <Table
                rowKey="id"
                size="small"
                dataSource={group.rows}
                columns={groupRangeColumns}
                pagination={false}
                scroll={{ x: "max-content" }}
              />
            ),
          }))}
        />
      )}

      {/* Create a brand-new group (lockedTypes null, one blank range) or edit
          an existing one as a whole (lockedTypes set + pre-filled, every
          current range pre-filled with its id) — adding, editing, and
          removing ranges all happen here, diffed and saved together. */}
      <Modal
        open={ruleSetOpen}
        title={lockedTypes ? "Edit Group" : "Add Rule Set"}
        onCancel={() => setRuleSetOpen(false)}
        onOk={handleRuleSetSubmit}
        okText="Save"
        okButtonProps={{ loading: ruleSetSaving }}
        destroyOnHidden
        width={640}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: "calc(100vh - 160px)", display: "flex", flexDirection: "column", overflow: "hidden" } }}
      >
        {/* Flex column filling the height budget above: the type Selects +
            "Price Ranges" label take whatever height they naturally need
            (flexShrink: 0), and the ranges list gets exactly what's left
            (flex: 1, minHeight: 0) and scrolls internally — rather than
            guessing a fixed pixel offset for the ranges list's own max
            height, which undercounted the header/footer/Selects chrome and
            let the modal grow past the viewport. */}
        <Form form={ruleSetForm} layout="vertical" style={{ marginTop: 12, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <div style={{ flexShrink: 0 }}>
            {DIMENSIONS.map(({ key, label }) => (
              <Form.Item
                key={key} name={key} label={label}
                dependencies={DIMENSIONS.filter(d => d.key !== key).map(d => d.key)}
                rules={[
                  ({ getFieldValue }) => ({
                    validator() {
                      const anySet = DIMENSIONS.some(d => getFieldValue(d.key) != null);
                      return anySet ? Promise.resolve() : Promise.reject(new Error("Select at least one of Customer Type, Make Type, or Parts Type."));
                    },
                  }),
                ]}
              >
                <Select placeholder={`Select ${label.toLowerCase()}`} allowClear disabled={!!lockedTypes} options={typeOptions[key]} optionFilterProp="label" />
              </Form.Item>
            ))}
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>Price Ranges</Typography.Text>
          </div>
          <Form.List
            name="ranges"
            rules={[{
              validator: async (_, ranges) => {
                if (!ranges || ranges.length < 1) return Promise.reject(new Error("Add at least one price range."));
              },
            }]}
          >
            {(fields, { add, remove }, { errors }) => (
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 8 }}>
                  {fields.map(({ key: fieldKey, name, ...restField }) => (
                    <div key={fieldKey} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                      <Form.Item {...restField} name={[name, "id"]} hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "MinPrice"]} rules={[{ required: true, message: "Required" }]} style={{ flex: 1, marginBottom: 0 }}>
                        <InputNumber min={0} placeholder="Min Price" style={{ width: "100%" }} />
                      </Form.Item>
                      <Form.Item
                        {...restField} name={[name, "MaxPrice"]} style={{ flex: 1, marginBottom: 0 }}
                        rules={[
                          { required: true, message: "Required" },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const min = getFieldValue(["ranges", name, "MinPrice"]);
                              if (value == null || min == null || value >= min) return Promise.resolve();
                              return Promise.reject(new Error("Must be ≥ Min"));
                            },
                          }),
                        ]}
                      >
                        <InputNumber min={0} placeholder="Max Price" style={{ width: "100%" }} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "DeliveryCost"]} rules={[{ required: true, message: "Required" }]} style={{ flex: 1, marginBottom: 0 }}>
                        <InputNumber min={0} placeholder="Delivery Cost" style={{ width: "100%" }} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "GP_Percentage"]} rules={[{ required: true, message: "Required" }]} style={{ flex: 1, marginBottom: 0 }}>
                        <InputNumber min={0} placeholder="GP %" style={{ width: "100%" }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button aria-label="Remove range" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      )}
                    </div>
                  ))}
                  <Form.ErrorList errors={errors} />
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                    Add Another Range
                  </Button>
                </div>
            )}
          </Form.List>
        </Form>
      </Modal>

      <ManageTypesTabsModal
        open={manageTypesOpen}
        onClose={() => { setManageTypesOpen(false); loadAll(); }}
        dimensions={DIMENSIONS}
      />
    </div>
  );
}
